'use client';

import React, { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { COLORS } from '@/constants/colors';
import { formatPriceWithCurrency } from '@/utils/formatters';
import AddToCart from './AddToCart';
import LazySection from './LazySection';

const sortIcon = '/assets/download.gif';
const upperIcon = '/assets/upperIcon.gif';
const downIcon = '/assets/downIcon.gif';

interface Header {
  id: number;
  label: string;
  name: string;
  maxWidth: number;
}

interface TableData {
  id?: number;
  gfu_part_num?: string;
  [key: string]: any;
}

interface FilterTableProps {
  headers: Header[];
  tableData: TableData[];
  defaultOrderBy?: string;
  defaultOrder?: 'asc' | 'desc';
  showAddToCart?: boolean;
}

interface EnhancedTableHeadProps {
  order: 'asc' | 'desc';
  orderBy: string;
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
  headers: Header[];
  showAddToCart?: boolean;
}

// Measurements come back from the API as strings ("9.00", "14.00"); compare them as
// numbers so 14" sorts after 6.33" instead of before it.
function toNumber(value: unknown): number | null {
  const text = String(value ?? '').trim();
  return /^-?\d+(\.\d+)?\s*"?$/.test(text) ? parseFloat(text) : null;
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  const numA = toNumber(a[orderBy]);
  const numB = toNumber(b[orderBy]);
  if (numA !== null && numB !== null) {
    return numB - numA;
  }
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: 'asc' | 'desc',
  orderBy: Key
): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function EnhancedTableHead(props: EnhancedTableHeadProps) {
  const { order, orderBy, onRequestSort, headers, showAddToCart } = props;
  const createSortHandler = (property: string) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <thead>
      <tr>
        {headers.map((header) => (
          <HeadCell
            key={header.id}
            style={{
              width: header.maxWidth,
              backgroundImage:
                orderBy === header.name && order === 'asc'
                  ? `url(${upperIcon})`
                  : orderBy === header.name && order === 'desc'
                  ? `url(${downIcon})`
                  : `url(${sortIcon})`,
              backgroundColor: orderBy === header.name ? COLORS.gray800 : COLORS.brandBlack,
              color: COLORS.white,
            }}
            onClick={createSortHandler(header.name)}
          >
            <div className="tablesorter-header-inner">{header.label}</div>
          </HeadCell>
        ))}
        {showAddToCart && (
          <StickyHeadCell
            style={{
              width: 180,
              backgroundColor: COLORS.brandBlack,
              color: COLORS.white,
              cursor: 'default',
              backgroundImage: 'none',
            }}
          >
            Add to Cart
          </StickyHeadCell>
        )}
      </tr>
    </thead>
  );
}

function AddToCartCell({ row, isEven }: { row: TableData; isEven: boolean }) {
  const [cartUnavailable, setCartUnavailable] = useState(false);
  const canAddToCart = Boolean(row.buy_url) && !cartUnavailable;

  return (
    <StickyBodyCell $isFirst={false} $isEven={isEven} onClick={(e) => e.stopPropagation()}>
      {canAddToCart ? (
        <LazySection rootMargin="400px" placeholderHeight="40px">
          <AddToCart
            buyButtonId={row.buy_url as string}
            id={String(row.gfu_part_num)}
            showOptions={false}
            hidePrice={false}
            priceSize="16px"
            compareAtSize="12px"
            compact
            onError={() => setCartUnavailable(true)}
          />
        </LazySection>
      ) : (
        formatPriceWithCurrency(row.price ?? 0)
      )}
    </StickyBodyCell>
  );
}

const FilterTable: React.FC<FilterTableProps> = ({
  headers,
  tableData,
  defaultOrderBy = 'calories',
  defaultOrder = 'asc',
  showAddToCart = false,
}) => {
  const router = useRouter();
  const [order, setOrder] = useState<'asc' | 'desc'>(defaultOrder);
  const [orderBy, setOrderBy] = useState<string>(defaultOrderBy);

  const handleRequestSort = useCallback((event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  }, [order, orderBy]);

  const handleClick = useCallback((row: TableData) => {
    if (row?.id && row?.gfu_part_num) {
      router.push(`/store/filter/${row.gfu_part_num}`);
    }
  }, [router]);

  // Memoize sorted data to prevent re-sorting on every render
  const sortedData = useMemo(
    () => stableSort(tableData, getComparator(order, orderBy)),
    [tableData, order, orderBy]
  );

  return (
    <FilterTableWrapper>
      <div className="fakeiframe">
        <TableContainer>
          <StyledTable>
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              headers={headers}
              showAddToCart={showAddToCart}
            />
            <tbody>
              {sortedData.map((row, id) => {
                return (
                  <BodyRow
                    key={id}
                    $isEven={id % 2 === 0}
                    onClick={() => handleClick(row)}
                  >
                    {headers.map((header, index) => {
                      const value = row[header.name];
                      const parts = typeof value === 'string' ? value.split(' - ').map((part) => part.trim()).filter(Boolean) : [];

                      return (
                        <BodyCell
                          key={header.id}
                          $isFirst={index === 0}
                        >
                          {index === 0 && row?.gfu_part_num ? (
                            <RowLink href={`/store/filter/${row.gfu_part_num}`}>
                              {value}
                            </RowLink>
                          ) : parts.length > 1 ? (
                            <ValueList>
                              {parts.map((part) => (
                                <ValueChip key={part}>{part}</ValueChip>
                              ))}
                            </ValueList>
                          ) : (
                            value
                          )}
                        </BodyCell>
                      );
                    })}
                    {showAddToCart && <AddToCartCell row={row} isEven={id % 2 === 0} />}
                  </BodyRow>
                );
              })}
            </tbody>
          </StyledTable>
        </TableContainer>
      </div>
    </FilterTableWrapper>
  );
};

export default FilterTable;

const FilterTableWrapper = styled.div`
  .fakeiframe {
    width: 100%;
    margin-bottom: 50px;
  }
`;

const TableContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  font: 14px/20px Arial, Sans-serif;
  border: #cdcdcd 1px solid;
  padding: 0;
  margin: 0 auto;
  overflow-x: auto;
`;

const StyledTable = styled.table`
  min-width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
`;

const HeadCell = styled.th`
  padding: 8px 6px;
  border: #cdcdcd 1px solid;
  font: 14px/20px Arial, Sans-serif;
  font-weight: 700;
  cursor: pointer;
  background: no-repeat 5px center;
  white-space: normal;

  .tablesorter-header-inner {
    padding-left: 23px;
  }
`;

const BodyRow = styled.tr<{ $isEven: boolean }>`
  background: ${props => props.$isEven ? COLORS.gray200 : COLORS.white};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: ${props => props.$isEven ? COLORS.gray400 : COLORS.gray300};
  }
`;

const BodyCell = styled.td<{ $isFirst: boolean }>`
  padding: 8px 6px;
  border: #cdcdcd 1px solid;
  font: 14px/20px Arial, Sans-serif;
  font-weight: 600;
  color: ${props => props.$isFirst ? COLORS.gray800 : COLORS.brandBlack};
  vertical-align: top;
`;

/* Pinned to the right edge of the table's scroll container so the Add to Cart
   column stays fully visible without requiring users to scroll a 10-column table. */
const StickyHeadCell = styled(HeadCell)`
  position: sticky;
  right: 0;
  z-index: 2;
  box-shadow: -2px 0 4px rgba(0, 0, 0, 0.15);
`;

const StickyBodyCell = styled(BodyCell)<{ $isEven: boolean }>`
  position: sticky;
  right: 0;
  z-index: 1;
  width: 180px;
  min-width: 180px;
  max-width: 180px;
  background: ${props => props.$isEven ? COLORS.gray200 : COLORS.white};
  box-shadow: -2px 0 4px rgba(0, 0, 0, 0.08);

  ${BodyRow}:hover & {
    background: ${props => props.$isEven ? COLORS.gray400 : COLORS.gray300};
  }
`;

const ValueList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const ValueChip = styled.span`
  font: 12px/16px Arial, Sans-serif;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  background: ${COLORS.gray300};
  border: 1px solid ${COLORS.border};
  white-space: nowrap;
`;

const RowLink = styled(Link)`
  display: block;
  color: inherit;
  text-decoration: none;
`;
